import { useState } from 'react';
import { Trans, Translation, useTranslation } from 'react-i18next';

import { Button, ModalFooter, LoadingOverlay, Anchor } from '@any-ui-react/core';
import { useModals } from '@any-ui-react/modals';

import { ChatAvatar } from '@anyx/feature-crm';
import { QuestionCircleIcon } from '@anyx/shared-icons';

type TestStaff = {
  id: string;
  avatar: string;
  name: string;
};

type AvailablePlatform = 'line' | 'facebook';

interface NoStaffModalContentProps {
  redirectChatPage?: () => void;
  availablePlatform?: AvailablePlatform;
}

interface WithStaffModalDescriptionProps {
  availablePlatform?: AvailablePlatform;
}

const WithStaffModalDescription = ({ availablePlatform }: WithStaffModalDescriptionProps) => {
  const { t } = useTranslation('anychat');
  if (availablePlatform === 'facebook') {
    return (
      <div>
        <p>{t('anychat.shared.dialogs.test.bulletDesc.desc')}</p>
        <ul className="list-inside list-disc">
          <Trans parent="li" ns="anychat" i18nKey="anychat.shared.dialogs.bulletDesc.bullet1">
            Accounts that have attached <span className="anc-staff-tag">Staff</span> tag.
          </Trans>
          <div className="flex items-center gap-x-1">
            <QuestionCircleIcon className="text-gray-5 h-4 w-4" />
            <Anchor
              className="underline"
              href="https://anymind-group.gitbook.io/anychat-help-page-ja/broadcast/test_message"
              target="_blank"
              rel="noreferrer"
            >
              {t('anychat.shared.dialogs.test.desc3')}
            </Anchor>
          </div>
          <Trans parent="li" ns="anychat" i18nKey="anychat.shared.dialogs.bulletDesc.bullet2">
            Accounts that initiated conversations within{' '}
            <span className="font-semibold">7 days</span>. If you don't see your account below, try
            sending a message to the Facebook Messenger account connected to this store.
          </Trans>
        </ul>
      </div>
    );
  }

  return (
    <>
      <Trans i18nKey="anychat.shared.dialogs.test.desc2" ns="anychat">
        You can send test messages to users who have the&nbsp;
        <span className="anc-staff-tag">Staff</span> tag.
      </Trans>
      <div className="flex items-center gap-x-1">
        <QuestionCircleIcon className="text-gray-5 h-4 w-4" />
        <Anchor
          className="underline"
          href="https://anymind-group.gitbook.io/anychat-help-page-ja/broadcast/test_message"
          target="_blank"
          rel="noreferrer"
        >
          {t('anychat.shared.dialogs.test.desc3')}
        </Anchor>
      </div>
    </>
  );
};

const NoStaffModalContent = ({
  redirectChatPage,
  availablePlatform = 'line',
}: NoStaffModalContentProps) => {
  const { t } = useTranslation('anychat');
  const platform = t('anychat.shared.platform', { availablePlatform });
  return (
    <>
      <div className="space-y-4">
        <Trans parent="p" i18nKey="anychat.shared.dialogs.noTestStaff.desc1" ns="anychat">
          You need to attach <span className="anc-staff-tag">Staff</span> tag to the user in order
          to receive this test message.
        </Trans>

        <Trans parent="p" i18nKey="anychat.shared.dialogs.noTestStaff.desc2" ns="anychat">
          Test members need to be a friend of this store's {{ platform }} official account before
          able to attach <span className="anc-staff-tag">Staff</span> tag.
        </Trans>

        <img
          loading="lazy"
          className="mx-auto mt-8 w-[400px]"
          src="/assets/anychat/add-tags-example.png"
          alt="Example of adding tags"
        />
      </div>
      <ModalFooter>
        <Button variant="default" onClick={redirectChatPage}>
          {t('anychat.shared.dialogs.noTestStaff.confirm')}
        </Button>
      </ModalFooter>
    </>
  );
};

interface WithStaffModalContentProps {
  testStaff: TestStaff[];
  onConfirm: () => Promise<boolean>;
  onClose: () => void;
  availablePlatform?: AvailablePlatform;
}

const WithStaffModalContent = ({
  testStaff,
  onConfirm,
  onClose,
  availablePlatform,
}: WithStaffModalContentProps) => {
  const { t } = useTranslation(['anychat', 'shared']);
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (sending) return;

    setSending(true);
    const success = await onConfirm();
    setSending(false);

    if (success) {
      onClose();
    }
  };

  return (
    <>
      <LoadingOverlay visible={sending} overlayProps={{ blur: 2 }} zIndex={9999} />
      <div className="text-sm">
        <p className="mb-4">{t('anychat.shared.dialogs.test.desc1')}</p>
        <WithStaffModalDescription availablePlatform={availablePlatform} />

        <ul className="shadow-dropdown mx-auto mt-8 max-h-64 w-2/3 divide-y overflow-auto rounded-lg">
          {testStaff.map(({ id, avatar, name }) => (
            <li className="flex items-center gap-x-2 px-4 py-2" key={id}>
              <ChatAvatar src={avatar} alt={name} />
              <span className="text-gray-9 text-sm">{name}</span>
            </li>
          ))}
        </ul>
      </div>
      <ModalFooter>
        <Button variant="default" onClick={onClose}>
          {t('shared.button.cancel', { ns: 'shared' })}
        </Button>
        <Button onClick={handleSend}>{t('anychat.shared.dialogs.test.confirm')}</Button>
      </ModalFooter>
    </>
  );
};

export type UseTestMessageModalProps = NoStaffModalContentProps & {
  fetchTestStaffList?: () => TestStaff[] | Promise<TestStaff[]>;
  availablePlatform?: 'line' | 'facebook';
};

const MODAL_ID = 'test-message-modal';

export const useTestMessageModal = ({
  redirectChatPage,
  fetchTestStaffList,
  availablePlatform,
}: UseTestMessageModalProps) => {
  const { openModal, closeModal } = useModals();

  const openNoStaffModal = () => {
    openModal({
      modalId: MODAL_ID,
      size: 'lg',
      title: (
        <Translation ns="anychat">
          {(t) => <>{t('anychat.shared.dialogs.noTestStaff.title')}</>}
        </Translation>
      ),
      children: (
        <NoStaffModalContent
          availablePlatform={availablePlatform}
          redirectChatPage={() => {
            redirectChatPage?.();
            closeModal(MODAL_ID);
          }}
        />
      ),
    });
  };

  const openWithStaffModal = (props: Omit<WithStaffModalContentProps, 'onClose'>) => {
    openModal({
      modalId: MODAL_ID,
      closeOnClickOutside: false,
      closeOnEscape: false,
      title: (
        <Translation ns="anychat">
          {(t) => <>{t('anychat.shared.dialogs.test.title')}</>}
        </Translation>
      ),
      children: <WithStaffModalContent {...props} onClose={() => closeModal(MODAL_ID)} />,
    });
  };

  const openTestMessageModal = async (onConfirm: WithStaffModalContentProps['onConfirm']) => {
    const testStaff = (await fetchTestStaffList?.()) || [];

    if (testStaff.length === 0) {
      openNoStaffModal();
      return;
    }

    openWithStaffModal({ testStaff, onConfirm, availablePlatform });
  };

  return { openTestMessageModal };
};
